import api from "./client";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function unwrapData(response) {
  if (response && typeof response === "object" && "data" in response) {
    return response.data;
  }

  return response;
}

function isNumericReference(value) {
  return /^\d+$/.test(String(value ?? "").trim());
}

export function formatConsultationCurrency(amount, currency = "USD") {
  const value = Number(amount ?? 0);

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "USD").toUpperCase(),
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `${value.toFixed(value % 1 === 0 ? 0 : 2)} ${String(currency || "USD").toUpperCase()}`;
  }
}

export function getConsultationTitle(consultation, isAr = false) {
  if (!consultation) return "";

  return isAr
    ? consultation.title_ar || consultation.title_en || `Consultation #${consultation.id ?? ""}`
    : consultation.title_en || consultation.title_ar || `Consultation #${consultation.id ?? ""}`;
}

export function getConsultationSummary(consultation, isAr = false) {
  if (!consultation) return "";

  return isAr
    ? consultation.short_description_ar || consultation.description_ar || consultation.short_description_en || consultation.description_en || ""
    : consultation.short_description_en || consultation.description_en || consultation.short_description_ar || consultation.description_ar || "";
}

export function getConsultationDescription(consultation, isAr = false) {
  if (!consultation) return "";

  return isAr
    ? consultation.description_ar || consultation.description_en || ""
    : consultation.description_en || consultation.description_ar || "";
}

export function getConsultationDurationLabel(consultation, isAr = false) {
  const minutes = Number(consultation?.duration_minutes ?? 0);
  if (!minutes) {
    return isAr ? "مرنة" : "Flexible";
  }

  return isAr ? `${minutes} دقيقة` : `${minutes} min`;
}

export function getConsultantName(consultation, isAr = false) {
  if (!consultation) return isAr ? "مستشار" : "Consultant";

  const user = consultation?.user || consultation?.consultant;
  if (user?.name) {
    return user.name;
  }

  return isAr ? "مستشار" : "Consultant";
}

export function formatConsultationDateTime(value, lang = "en") {
  if (!value) return "";

  try {
    return new Date(value).toLocaleString(lang === "ar" ? "ar" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

export async function listConsultations(options = {}) {
  const response = await api.apiGet("consultations", {
    cache: options.cache ?? true,
    ttl: options.ttl ?? 30_000,
  });

  return asArray(unwrapData(response));
}

export async function resolveConsultationId(consultationReference, knownConsultations = null) {
  const raw = String(consultationReference ?? "").trim();

  if (isNumericReference(raw)) {
    return Number(raw);
  }

  const consultations =
    knownConsultations || (await listConsultations({ cache: true, ttl: 15_000 }));
  const normalized = raw.toLowerCase();
  const matched = consultations.find(
    (consultation) =>
      String(consultation?.slug || "").toLowerCase() === normalized ||
      String(consultation?.id || "") === raw
  );

  if (!matched?.id) {
    throw { status: 404, message: "Consultation not found" };
  }

  return Number(matched.id);
}

export async function getConsultationDetails(consultationReference) {
  const resolvedId = isNumericReference(consultationReference)
    ? Number(consultationReference)
    : await resolveConsultationId(consultationReference);

  const response = await api.apiGet(`consultations/${resolvedId}`);
  return unwrapData(response);
}

export async function listConsultationReviews(consultationReference) {
  const resolvedId = isNumericReference(consultationReference)
    ? Number(consultationReference)
    : await resolveConsultationId(consultationReference);

  const response = await api.apiGet(`consultations/${resolvedId}/reviews`, {
    cache: true,
    ttl: 15_000,
  });

  return asArray(unwrapData(response));
}

export async function bookConsultation(consultationId, payload = {}) {
  return api.apiPost(`consultations/${consultationId}/book`, payload);
}

export async function createConsultationCheckout(consultationId, payload = {}) {
  return api.apiPost(`consultations/${consultationId}/checkout`, payload);
}

export async function getMyConsultations() {
  const response = await api.apiGet("my-consultations");
  return asArray(unwrapData(response));
}

export async function submitConsultationReview(consultationId, payload = {}) {
  const response = await api.apiPost(`consultations/${consultationId}/reviews`, payload);
  return unwrapData(response);
}

export async function confirmConsultationPayment({ sessionId = "", token = "", provider = "", mode = "" } = {}) {
  const params = new URLSearchParams();

  if (sessionId) params.set("session_id", sessionId);
  if (token) params.set("token", token);
  if (provider) params.set("provider", provider);
  if (mode) params.set("mode", mode);

  const response = await api.apiGet(
    `consultations/payment/success?${params.toString()}`
  );

  return unwrapData(response);
}
